import {
  Avatar,
  AvatarFallback,
  Badge,
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeftIcon,
  Clock3Icon,
  CopyIcon,
  ExternalLinkIcon,
  Loader2Icon,
  MessageCirclePlusIcon,
  PhoneIcon,
} from 'lucide-react'
import { useMemo, useState } from 'react'
import { toast } from 'sonner'

import { AppHeader } from '@/components/app-header'
import { useInboxChannels } from '@/hooks/use-attendance-options'
import { useAuth } from '@/hooks/use-auth'
import {
  useContact,
  useContactConversations,
  useStartContactConversation,
} from '@/hooks/use-contacts'
import { formatListTime, getInitials, previewText } from '@/lib/format'

export const Route = createFileRoute('/_app/contatos/$contactId')({
  component: ContactDetailPage,
})

function copy(value: string | null | undefined, label: string) {
  if (!value) return
  void navigator.clipboard.writeText(value)
  toast.success(`${label} copiado.`)
}

function whatsappUrl(phone: string | null | undefined): string | null {
  const digits = phone?.replace(/\D/g, '')
  if (!digits) return null
  const normalized = digits.startsWith('55') ? digits : `55${digits}`
  return `https://wa.me/${normalized}`
}

function ContactDetailPage() {
  const { contactId } = Route.useParams()
  const navigate = useNavigate()
  const { currentOrgId } = useAuth()
  const [selectedChannelId, setSelectedChannelId] = useState('')

  const contact = useContact(currentOrgId, contactId)
  const conversations = useContactConversations(currentOrgId, contactId)
  const channels = useInboxChannels(currentOrgId)
  const startConversation = useStartContactConversation(currentOrgId)
  const connectedChannels = useMemo(
    () =>
      (channels.data ?? []).filter((channel) => channel.status === 'CONNECTED'),
    [channels.data]
  )

  const channelId = selectedChannelId || connectedChannels[0]?.id || ''
  const name =
    contact.data?.name ??
    contact.data?.phone ??
    contact.data?.email ??
    'Contato'
  const phone = contact.data?.phone ?? contact.data?.phoneRaw ?? null
  const externalWhatsappUrl = whatsappUrl(phone)
  const latestConversations = useMemo(
    () => conversations.data?.slice(0, 12) ?? [],
    [conversations.data]
  )

  function handleStartConversation() {
    if (!contact.data || !phone || !channelId) return
    startConversation.mutate({
      channelId,
      phone,
      name,
    })
  }

  return (
    <div className="lg:border-border flex min-h-0 flex-1 flex-col lg:mx-auto lg:w-full lg:max-w-3xl lg:border-x">
      <AppHeader
        title="Contato"
        leading={
          <Button
            variant="ghost"
            size="icon"
            className="size-9"
            onClick={() => navigate({ to: '/contatos' })}
            aria-label="Voltar"
          >
            <ArrowLeftIcon className="size-5" />
          </Button>
        }
      />

      {contact.isLoading ? (
        <div className="flex flex-1 items-center justify-center">
          <Loader2Icon className="text-muted-foreground size-6 animate-spin" />
        </div>
      ) : !contact.data ? (
        <div className="text-muted-foreground flex flex-1 items-center justify-center text-sm">
          Contato não encontrado
        </div>
      ) : (
        <div className="min-h-0 flex-1 overflow-y-auto">
          <section className="border-border bg-card border-b px-4 py-5">
            <div className="flex items-start gap-3">
              <Avatar className="size-14 shrink-0">
                <AvatarFallback className="bg-primary/10 text-primary text-base font-semibold">
                  {getInitials(name)}
                </AvatarFallback>
              </Avatar>
              <div className="min-w-0 flex-1">
                <h1 className="truncate text-base font-semibold">{name}</h1>
                {phone ? (
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
                    <button
                      type="button"
                      onClick={() => copy(phone, 'Telefone')}
                      className="text-muted-foreground hover:text-foreground flex max-w-full items-center gap-1 text-sm"
                    >
                      <PhoneIcon className="size-4 shrink-0" />
                      <span className="truncate">{phone}</span>
                      <CopyIcon className="size-3.5 shrink-0" />
                    </button>
                    {externalWhatsappUrl ? (
                      <a
                        href={externalWhatsappUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="text-primary inline-flex items-center gap-1 text-xs font-medium"
                      >
                        WhatsApp externo
                        <ExternalLinkIcon className="size-3" />
                      </a>
                    ) : null}
                  </div>
                ) : null}
                {contact.data.email ? (
                  <button
                    type="button"
                    onClick={() => copy(contact.data?.email, 'E-mail')}
                    className="text-muted-foreground hover:text-foreground mt-1 block max-w-full truncate text-sm"
                  >
                    {contact.data.email}
                  </button>
                ) : null}
              </div>
            </div>

            {contact.data.tags.length > 0 ? (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {contact.data.tags.map((tag) => (
                  <Badge
                    key={tag.id}
                    variant="outline"
                    style={{ borderColor: tag.color, color: tag.color }}
                  >
                    {tag.name}
                  </Badge>
                ))}
              </div>
            ) : null}
          </section>

          <section className="space-y-3 border-b px-4 py-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Atendimento</p>
                <p className="text-muted-foreground text-xs">
                  Inicie pelo canal conectado da operação.
                </p>
              </div>
              {contact.data.kanbanCard?.stage ? (
                <Badge variant="secondary">
                  {contact.data.kanbanCard.stage.name}
                </Badge>
              ) : null}
            </div>

            <div className="flex gap-2">
              <Select
                value={channelId || undefined}
                onValueChange={setSelectedChannelId}
                disabled={connectedChannels.length === 0}
              >
                <SelectTrigger className="min-w-0 flex-1">
                  <SelectValue placeholder="Selecionar canal" />
                </SelectTrigger>
                <SelectContent>
                  {connectedChannels.map((channel) => (
                    <SelectItem key={channel.id} value={channel.id}>
                      {channel.name} ({channel.provider})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                type="button"
                onClick={handleStartConversation}
                disabled={
                  !phone ||
                  !channelId ||
                  startConversation.isPending ||
                  channels.isLoading
                }
              >
                {startConversation.isPending ? (
                  <Loader2Icon className="size-4 animate-spin" />
                ) : (
                  <MessageCirclePlusIcon className="size-4" />
                )}
                Iniciar
              </Button>
            </div>
            {!phone ? (
              <p className="text-destructive text-xs">
                Este contato não possui telefone para iniciar atendimento.
              </p>
            ) : null}
            {!channels.isLoading && connectedChannels.length === 0 ? (
              <p className="text-muted-foreground text-xs">
                Nenhum canal conectado disponível.
              </p>
            ) : null}
          </section>

          <section className="px-4 py-4">
            <div className="mb-3 flex items-center gap-2">
              <Clock3Icon className="text-muted-foreground size-4" />
              <p className="text-sm font-semibold">Histórico recente</p>
            </div>
            {conversations.isLoading ? (
              <div className="flex justify-center py-8">
                <Loader2Icon className="text-muted-foreground size-5 animate-spin" />
              </div>
            ) : latestConversations.length === 0 ? (
              <p className="text-muted-foreground py-6 text-center text-sm">
                Nenhum atendimento encontrado para este contato.
              </p>
            ) : (
              <div className="divide-border overflow-hidden rounded-md border">
                {latestConversations.map((conversation) => {
                  const lastMessage = conversation.lastMessage
                  return (
                    <button
                      key={conversation.id}
                      type="button"
                      onClick={() =>
                        navigate({
                          to: '/conversas/$conversationId',
                          params: { conversationId: conversation.id },
                        })
                      }
                      className="hover:bg-muted/60 flex w-full items-start gap-3 border-b px-3 py-3 text-left last:border-b-0"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate text-sm font-medium">
                            {conversation.channel?.name ?? 'Canal'}
                          </p>
                          <Badge
                            variant="outline"
                            className="shrink-0 text-[10px]"
                          >
                            {conversation.status}
                          </Badge>
                        </div>
                        <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                          {lastMessage
                            ? previewText(lastMessage.content, lastMessage.type)
                            : 'Sem mensagens'}
                        </p>
                      </div>
                      <span className="text-muted-foreground shrink-0 text-xs">
                        {formatListTime(
                          conversation.lastMessageAt ?? conversation.openedAt
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  )
}
